import React, { useEffect, useRef, useState } from 'react';
import { TomTomConfig } from '@tomtom-org/maps-sdk/core';
import { TomTomMap } from '@tomtom-org/maps-sdk/map';
import { exploreService } from './exploreService';

const MapComponent = ({ 
  latitude, 
  longitude, 
  places = [], 
  zoom = 15,
  showDirections = false,
  initialDestination = null 
}) => {
  const mapContainer = useRef(null);
  const mapInstance = useRef(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [apiStatus, setApiStatus] = useState('checking');

  // Initialize TomTom SDK
  useEffect(() => {
    const apiKey = process.env.REACT_APP_TOMTOM_API_KEY || exploreService.tomtomKey;
    
    if (!apiKey || apiKey === 'YOUR_TOMTOM_API_KEY') {
      console.error('TomTom API key is missing');
      setApiStatus('invalid');
      return;
    }

    try {
      TomTomConfig.instance.put({ 
        apiKey: apiKey, 
        language: 'en-US'
      });
      setApiStatus('valid');
    } catch (error) {
      console.error('Failed to initialize TomTom config:', error);
      setApiStatus('error');
    }
  }, []);

  // Initialize map
  useEffect(() => {
    if (apiStatus !== 'valid' || !mapContainer.current) return;

    const initMap = async () => {
      try {
        // Create a unique container ID
        const containerId = mapContainer.current.id || `map-${Date.now()}`;
        if (!mapContainer.current.id) {
          mapContainer.current.id = containerId;
        }

        console.log('Initializing TomTom map...');

        // Create the map - this might return a promise
        let map;
        
        // Try different approaches
        try {
          // Approach 1: Direct instantiation
          map = new TomTomMap({
            container: containerId,
            center: longitude && latitude ? [longitude, latitude] : [-74.5, 40],
            zoom: zoom,
            style: 'tomtom/basic-main'
          });
          
          // Check if map has .on method
          if (map && typeof map.on === 'function') {
            // Traditional MapLibre style events
            map.on('load', () => {
              console.log('Map loaded with .on() events');
              setMapLoaded(true);
            });
          } else if (map && typeof map.then === 'function') {
            // Might be a Promise
            map.then((loadedMap) => {
              console.log('Map loaded from Promise');
              mapInstance.current = loadedMap;
              setMapLoaded(true);
            }).catch(error => {
              console.error('Map promise rejected:', error);
              showMapError(error.message);
            });
          } else {
            // Assume it's already loaded
            console.log('Map created (no events detected)');
            mapInstance.current = map;
            setMapLoaded(true);
          }
        } catch (error) {
          console.error('Map creation error:', error);
          showMapError(error.message);
        }

      } catch (error) {
        console.error('Failed to initialize map:', error);
        showMapError(error.message);
      }
    };

    const showMapError = (errorMessage) => {
      if (mapContainer.current) {
        mapContainer.current.innerHTML = `
          <div style="padding: 40px 20px; text-align: center; background: #f8f9fa; border-radius: 12px; height: 100%; display: flex; flex-direction: column; justify-content: center; align-items: center;">
            <div style="font-size: 48px; margin-bottom: 16px;">🗺️</div>
            <h3 style="font-size: 18px; font-weight: 600; color: #333; margin-bottom: 8px;">Map Error</h3>
            <p style="color: #666; margin-bottom: 24px; max-width: 500px;">${errorMessage || 'Unable to load map'}</p>
            <button 
              onclick="window.location.reload()"
              style="padding: 10px 20px; background: #4F46E5; color: white; border: none; border-radius: 8px; font-weight: 600; cursor: pointer;"
            >
              Retry
            </button>
          </div>
        `;
      }
    };

    initMap();

    return () => {
      if (mapInstance.current && typeof mapInstance.current.remove === 'function') {
        mapInstance.current.remove();
      }
      mapInstance.current = null;
    };
  }, [apiStatus, latitude, longitude, zoom]);

  // Add markers when map loads
  useEffect(() => {
    if (!mapLoaded || !mapInstance.current || !places.length) return;

    console.log('Adding markers to map...');
    
    // Simple marker adding logic
    places.forEach((place) => {
      if (place.coordinates?.latitude && place.coordinates?.longitude) {
        try {
          // Create a simple marker element
          const markerElement = document.createElement('div');
          markerElement.innerHTML = place.icon || '📍';
          markerElement.style.cssText = `
            font-size: 24px;
            cursor: pointer;
            color: ${place.category === 'current' ? '#10B981' : '#EF4444'};
          `;

          // Check what methods are available on the map
          if (mapInstance.current.addMarker) {
            // New SDK method
            mapInstance.current.addMarker({
              element: markerElement,
              position: [place.coordinates.longitude, place.coordinates.latitude]
            });
          } else if (mapInstance.current.marker) {
            // Alternative method
            new mapInstance.current.Marker({ element: markerElement })
              .setLngLat([place.coordinates.longitude, place.coordinates.latitude])
              .addTo(mapInstance.current);
          }
        } catch (error) {
          console.error('Error adding marker:', error);
        }
      }
    });
  }, [mapLoaded, places]);

  return (
    <div style={{ width: '100%', height: '100%' }}>
      {apiStatus === 'checking' && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '500px',
          background: '#f8f9fa',
          borderRadius: '16px'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: '40px',
              height: '40px',
              border: '4px solid #e5e7eb',
              borderTop: '4px solid #4F46E5',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 16px'
            }} />
            <p style={{ color: '#6b7280' }}>Loading map...</p>
          </div>
        </div>
      )}
      
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>

      {apiStatus === 'valid' && (
        <div 
          ref={mapContainer}
          style={{ 
            width: '100%', 
            height: '500px', 
            borderRadius: '16px', 
            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)', 
            overflow: 'hidden'
          }}
        />
      )}

      {apiStatus === 'invalid' && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '500px',
          background: '#f8f9fa',
          borderRadius: '16px'
        }}>
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔑</div>
            <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#333', marginBottom: '8px' }}>
              API Key Required
            </h3>
            <p style={{ color: '#666', marginBottom: '24px' }}>
              Please set REACT_APP_TOMTOM_API_KEY in your .env file
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default MapComponent;